import Icon from "elements/Icon"
import InputSymphony from "elements/InputSymphony"
import Message from "elements/Message"
import TagSymphony from "elements/TagSymphony"
import useMaestroChatStore, { MessageItem } from "Hooks/useMaestroChat"
import useScreen from "Hooks/useScreen"
import Colors from "modules/Colors"
import Sty from "modules/Sty"
import { clsx, makeKey, styx } from "modules/Utils"
import { CSSProperties, useEffect, useRef, useState } from "react"
import { useConversation } from '@11labs/react'
import '../styles/animations.css'
import useCurrentBrand from "Hooks/useCurrentBrand"
import dayjs from "dayjs"
import { WebsiteType } from "modules/Const"

/** Agent ID for ElevenLabs voice AI */
const ELEVENLABS_AGENT_ID = 'AS15w0BLcmvbvhn897qi' // Replace with your actual agent ID

// Define ElevenLabs message types
interface ElevenLabsTranscriptMessage {
    type: 'transcript'
    text: string
    is_final: boolean
}

interface ElevenLabsResponseMessage {
    type: 'response'
    text: string
}

type ElevenLabsMessage = ElevenLabsTranscriptMessage | ElevenLabsResponseMessage | { message: string; source: string }

interface MaestroUserChatProps {
    className?: string
    style?: CSSProperties
}

const MaestroUserChat = ({
    className,
    style,
}: MaestroUserChatProps) => {
    const {
        cssCalc,
        mobileView,
        mStyle,
    } = useScreen()

    const {
        currentBrand,
        recentReleases
    } = useCurrentBrand()

    const {
        isChatOpen,
        setIsChatOpen,
        getMessages,
        messages,
        messageText,
        setMessageText,
        sendMessage,
        maestroTags,
        isLoadingMessages,
        // Voice AI state
        isVoiceEnabled,
        isVoiceListening,
        isVoiceSpeaking,
        voiceVolume,
        conversationId,
        isVoiceMode,
        // Streaming state
        isStreaming,
        currentStreamedText,
        // Voice AI actions
        setIsVoiceEnabled,
        setIsVoiceListening,
        setIsVoiceSpeaking,
        setVoiceVolume,
        setConversationId,
        setIsVoiceMode,
        setMessages,
        clearChatHistory,
        // Voice AI methods
        startVoiceSession,
        endVoiceSession,
        toggleVoice,
        adjustVoiceVolume
    } = useMaestroChatStore()

    // Local state for UI feedback
    const [transcriptText, setTranscriptText] = useState('')
    const [isProcessing, setIsProcessing] = useState(false)
    const [statusMessage, setStatusMessage] = useState('')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    // Initialize ElevenLabs conversation hook
    const conversation = useConversation({
        onConnect: () => {
            console.log('Voice conversation connected')
            setIsVoiceListening(true)
            setStatusMessage('Connected to voice service')
            setTimeout(() => setStatusMessage(''), 2000)
        },
        onDisconnect: () => {
            console.log('Voice conversation disconnected')
            setIsVoiceListening(false)
            setIsVoiceSpeaking(false)
            setStatusMessage('Voice service disconnected')
            setTimeout(() => setStatusMessage(''), 2000)
        },
        onMessage: (message: ElevenLabsMessage) => {
            console.log('Voice message received:', message)
            // Handle different message types
            if ('type' in message) {
                if (message.type === 'transcript') {
                    // Update transcript text (both final and interim)
                    setTranscriptText(message.text)

                    if (message.is_final) {
                        // Final user transcript
                        setTranscriptText('')

                        // Add user message to chat history
                        const userMessage: MessageItem = {
                            id: messages.length + 1,
                            text: message.text,
                            createdAt: new Date().toISOString(),
                            isUser: true
                        }
                        setMessages([...messages, userMessage])

                        // Process the message
                        setIsProcessing(true)
                        setStatusMessage('Processing your request...')
                    }
                } else if (message.type === 'response') {
                    // AI response
                    setIsProcessing(false)
                    setStatusMessage('')

                    // Add AI response to chat history
                    const newMessage: MessageItem = {
                        id: messages.length + 1,
                        text: message.text,
                        createdAt: new Date().toISOString(),
                        isUser: false
                    }
                    setMessages([...messages, newMessage])
                }
            } else if ('message' in message) {
                // Handle legacy message format
                console.log('Legacy message format:', message.message)

                // Add message to chat history if it contains useful content
                if (message.message && message.message.trim()) {
                    const newMessage: MessageItem = {
                        id: messages.length + 1,
                        text: message.message,
                        createdAt: new Date().toISOString(),
                        isUser: message.source === 'user'
                    }
                    setMessages([...messages, newMessage])
                }
            }
        },
        onError: (error) => {
            console.error('Voice conversation error:', error)
            setIsVoiceEnabled(false)
            setIsVoiceListening(false)
            setIsVoiceSpeaking(false)
            setStatusMessage('Error with voice service')
            setTimeout(() => setStatusMessage(''), 3000)
        },
        // Register website builder client tools
        clientTools: {
            // Create new website
            createWebsite: async ({ websiteType, name }) => {
                setStatusMessage('Creating new website...')
                try {
                    const result = await useMaestroChatStore.getState().createWebsite({ 
                        websiteType, 
                        name 
                    })
                    setStatusMessage(`Website created successfully!`)
                    setTimeout(() => setStatusMessage(''), 2000)
                    return result
                } catch (error) {
                    console.error('Error creating website:', error)
                    setStatusMessage('Failed to create website')
                    setTimeout(() => setStatusMessage(''), 3000)
                    return { error: 'Failed to create website', success: false }
                }
            },
            
            // Update website color
            updateWebsiteColor: async ({ websiteId, backgroundColor, secondaryColor }) => {
                setStatusMessage('Updating website colors...')
                try {
                    const result = await useMaestroChatStore.getState().updateWebsiteColor({
                        websiteId,
                        backgroundColor,
                        secondaryColor
                    })
                    setStatusMessage('Website colors updated!')
                    setTimeout(() => setStatusMessage(''), 2000)
                    return result
                } catch (error) {
                    console.error('Error updating website colors:', error)
                    setStatusMessage('Failed to update website colors')
                    setTimeout(() => setStatusMessage(''), 3000)
                    return { error: 'Failed to update website colors', success: false }
                }
            },
            
            // Update website font
            updateWebsiteFont: async ({ websiteId, fontFamily }) => {
                setStatusMessage('Updating website font...')
                try {
                    const result = await useMaestroChatStore.getState().updateWebsiteFont({
                        websiteId,
                        fontFamily
                    })
                    setStatusMessage('Website font updated!')
                    setTimeout(() => setStatusMessage(''), 2000)
                    return result
                } catch (error) {
                    console.error('Error updating website font:', error)
                    setStatusMessage('Failed to update website font')
                    setTimeout(() => setStatusMessage(''), 3000)
                    return { error: 'Failed to update website font', success: false }
                }
            },
            
            // Publish website
            publishWebsite: async ({ websiteId }) => {
                setStatusMessage('Publishing website...')
                try {
                    const result = await useMaestroChatStore.getState().publishWebsite({
                        websiteId
                    })
                    setStatusMessage('Website published successfully!')
                    setTimeout(() => setStatusMessage(''), 2000)
                    return result
                } catch (error) {
                    console.error('Error publishing website:', error)
                    setStatusMessage('Failed to publish website')
                    setTimeout(() => setStatusMessage(''), 3000)
                    return { error: 'Failed to publish website', success: false }
                }
            }
        }
    })

    // Update speaking state when ElevenLabs speaking state changes
    useEffect(() => {
        if (conversation.isSpeaking !== isVoiceSpeaking) {
            setIsVoiceSpeaking(conversation.isSpeaking)
            if (conversation.isSpeaking) {
                setStatusMessage('Maestro is speaking...')
            } else if (!isProcessing) {
                setStatusMessage('')
            }
        }
    }, [conversation.isSpeaking, isProcessing])

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages])

    // Implement voice session methods
    const handleStartVoiceSession = async () => {
        try {
            setStatusMessage('Requesting microphone access...')
            // Request microphone permission
            await navigator.mediaDevices.getUserMedia({ audio: true })

            setStatusMessage('Starting voice session...')
            // Start ElevenLabs session
            const id = await conversation.startSession({
                agentId: ELEVENLABS_AGENT_ID,
                dynamicVariables: {
                    date_string: dayjs().format('MMM D, YYYY h:mm A'),
                    brand_name: currentBrand?.name ?? '',
                    recent_releases: recentReleases?.map((release) => release.name).join(', ') ?? '',
                    // Website builder capabilities
                    available_website_types: Object.values(WebsiteType).join(', '),
                    available_font_families: 'Arial, Helvetica, Roboto, Montserrat, Open Sans, Lato, Poppins',
                    can_create_website: 'true',
                    can_update_website: 'true',
                    can_publish_website: 'true',
                },
            })

            setConversationId(id)
            setIsVoiceEnabled(true)

            // Set initial volume
            await conversation.setVolume({ volume: voiceVolume })
        } catch (error) {
            console.error('Failed to start voice session:', error)
            setIsVoiceEnabled(false)
            setStatusMessage('Failed to start voice session')
            setTimeout(() => setStatusMessage(''), 3000)
        }
    }

    const handleEndVoiceSession = async () => {
        try {
            setStatusMessage('Ending voice session...')
            await conversation.endSession()
            setIsVoiceEnabled(false)
            setConversationId(null)
            setStatusMessage('Voice session ended')
            setTimeout(() => setStatusMessage(''), 2000)
        } catch (error) {
            console.error('Failed to end voice session:', error)
            setStatusMessage('Error ending voice session')
            setTimeout(() => setStatusMessage(''), 3000)
        }
    }

    const handleVolumeChange = async (newVolume: number) => {
        try {
            await conversation.setVolume({ volume: newVolume })
            setVoiceVolume(newVolume)
        } catch (error) {
            console.error('Failed to adjust volume:', error)
        }
    }

    // Connect the store methods to the actual implementation
    useEffect(() => {
        const originalStartVoiceSession = startVoiceSession
        const originalEndVoiceSession = endVoiceSession
        const originalAdjustVoiceVolume = adjustVoiceVolume

        // Override the store methods with our implementations
        useMaestroChatStore.setState({
            startVoiceSession: handleStartVoiceSession,
            endVoiceSession: handleEndVoiceSession,
            adjustVoiceVolume: handleVolumeChange
        })

        // Restore original methods on unmount
        return () => {
            useMaestroChatStore.setState({
                startVoiceSession: originalStartVoiceSession,
                endVoiceSession: originalEndVoiceSession,
                adjustVoiceVolume: originalAdjustVoiceVolume
            })
        }
    }, [])

    useEffect(() => {
        getMessages()
    }, [])

    // Toggle voice mode
    const toggleVoiceMode = () => {
        if (isVoiceMode) {
            // Exit voice mode
            handleEndVoiceSession()
            setIsVoiceMode(false)
        } else {
            // Enter voice mode
            handleStartVoiceSession()
            setIsVoiceMode(true)
        }
    }

    // Add custom styles for typing animation
    useEffect(() => {
        const stylesElement = document.createElement('style');
        stylesElement.innerHTML = `
            .maestroMessageThinking {
                background-color: ${Colors.purpleLight} !important;
                color: ${Colors.purple} !important;
                font-style: italic;
            }
            
            @keyframes typing {
                0% { width: 0 }
                100% { width: 100% }
            }
            
            .maestroMessageStreaming .messageText {
                white-space: pre-wrap;
                overflow: hidden;
                border-right: 2px solid ${Colors.purple};
                animation: typing 0.5s steps(40, end);
            }
        `;
        document.head.appendChild(stylesElement);
        
        return () => {
            if (stylesElement && document.head.contains(stylesElement)) {
                document.head.removeChild(stylesElement);
            }
        };
    }, []);

    const renderMaestroChatHeader = () => {
        return (
            <div className="maestroUserChatHeader" style={sty.maestroUserChatHeader}>
                <Icon
                    className="maestroUserChatHeaderIcon"
                    icon="maestroBadgeImage"
                    size={32}
                />
                <div className="maestroUserChatHeaderTitle" style={Sty.text16bold}>
                    Ask Maestro AI anything about your fanbase
                </div>
                <div className="maestroUserChatHeaderActions" style={sty.maestroUserChatHeaderActions}>
                    <div
                        className="maestroUserChatHeaderActionButton"
                        style={sty.maestroUserChatHeaderActionButton}
                        onClick={clearChatHistory}
                        title="Clear chat history"
                    >
                        <Icon
                            className="maestroUserChatHeaderIcon"
                            icon="trash"
                            size={16}
                            color={Colors.greyDark}
                        />
                    </div>
                    <div className="maestroUserChatHeaderCloseIconCircle" style={sty.maestroUserChatHeaderCloseIconCircle}>
                        <Icon
                            className="maestroUserChatHeaderIcon"
                            icon="close"
                            size={20}
                            color={Colors.greyDark}
                            onClick={() => setIsChatOpen(false)}
                        />
                    </div>
                </div>
            </div>
        )
    }

    const renderStatusIndicator = () => {
        if (!statusMessage && !isLoadingMessages && !transcriptText && !isProcessing && !isVoiceSpeaking && !isStreaming) {
            return null
        }

        return (
            <div className="statusIndicatorContainer" style={sty.statusIndicatorContainer}>
                {(isLoadingMessages || isProcessing || isStreaming) && (
                    <div className="loadingIndicator" style={sty.loadingIndicator}>
                        <div className="loadingDot" style={styx(sty.loadingDot, sty.loadingDot1)}></div>
                        <div className="loadingDot" style={styx(sty.loadingDot, sty.loadingDot2)}></div>
                        <div className="loadingDot" style={styx(sty.loadingDot, sty.loadingDot3)}></div>
                    </div>
                )}

                {isVoiceSpeaking && (
                    <div className="speakingIndicator" style={sty.speakingIndicator}>
                        <div className="speakingWave" style={styx(sty.speakingWave, sty.speakingWave1)}></div>
                        <div className="speakingWave" style={styx(sty.speakingWave, sty.speakingWave2)}></div>
                        <div className="speakingWave" style={styx(sty.speakingWave, sty.speakingWave3)}></div>
                    </div>
                )}

                <div className="statusMessage" style={sty.statusMessage}>
                    {statusMessage || (isLoadingMessages ? 'thinking...' :
                        (isProcessing ? 'Processing...' :
                            (isVoiceSpeaking ? 'Maestro is speaking...' :
                                (transcriptText ? 'Listening...' : 
                                    (isStreaming ? 'Maestro is typing...' : '')))))}
                </div>

                {transcriptText && (
                    <div className="transcriptPreview" style={sty.transcriptPreview}>
                        "{transcriptText}"
                    </div>
                )}
            </div>
        )
    }

    const renderMessages = () => {
        return (
            <div
                className="maestroMessages"
                style={styx(
                    sty.maestroMessages,
                    { height: cssCalc(32 + 65 + 121) } // NOTE: padding + header + footer
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {messages.map((message, index) => {
                        return (
                            <Message
                                key={makeKey(message.id, index)}
                                className="maestroMessage"
                                text={message.text}
                                isUser={message.isUser}
                                createdAt={message.createdAt}
                            />
                        )
                    })}

                    {/* Show thinking bubble when loading */}
                    {isLoadingMessages && !isStreaming && (
                        <Message
                            key="thinking-message"
                            className="maestroMessage maestroMessageThinking"
                            text="thinking..."
                            isUser={false}
                            createdAt={new Date().toISOString()}
                        />
                    )}

                    {/* Render streaming text if available */}
                    {isStreaming && currentStreamedText && (
                        <Message
                            key="streaming-message"
                            className="maestroMessage maestroMessageStreaming"
                            text={currentStreamedText}
                            isUser={false}
                            createdAt={new Date().toISOString()}
                            isStreaming={true}
                        />
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {renderStatusIndicator()}
            </div>
        )
    }


    const renderVolumeControl = () => {
        return (

            <div className="voiceVolumeContainer" style={sty.voiceVolumeContainer}>
                <Icon
                    className="volumeIcon"
                    icon="volume"
                    size={16}
                    color={Colors.greyDark}
                />
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={voiceVolume}
                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    style={sty.volumeSlider}
                />
            </div>

        )
    }

    const renderVoiceControls = () => {
        if (!isVoiceEnabled && !isVoiceListening && !isVoiceSpeaking) {
            return (
                <div className="voiceControlsContainer" style={sty.voiceControlsContainer}>
                    <div
                        className="voiceControlButton"
                        style={sty.voiceControlButton}
                        onClick={toggleVoiceMode}
                    >
                        <Icon
                            className="voiceControlIcon"
                            icon="maestroBadgeImage"
                            size={20}
                            color={Colors.greyDark}
                        />
                        <span style={Sty.text14}>Enable Voice Chat</span>
                    </div>
                </div>
            )
        }



        return (
            <div className="voiceControlsContainer" style={sty.voiceControlsContainer}>
                <div
                    className="voiceControlButton"
                    style={styx(
                        sty.voiceControlButton,
                        isVoiceListening ? sty.voiceControlButtonActive : {}
                    )}
                    onClick={toggleVoice}
                >
                    <Icon
                        className="voiceControlIcon"
                        icon={"maestroBadgeImage"}
                        size={20}
                        color={isVoiceListening ? Colors.purple : Colors.greyDark}
                    />
                    <span style={styx(
                        Sty.text14,
                        isVoiceListening ? { color: Colors.purple } : {}
                    )}>
                        {isVoiceListening ? 'Listening...' : 'Enable Voice'}
                    </span>
                </div>

                {isVoiceMode && (
                    <div
                        className="exitVoiceModeButton"
                        style={sty.exitVoiceModeButton}
                        onClick={() => setIsVoiceMode(false)}
                    >
                        <Icon
                            className="exitVoiceModeIcon"
                            icon="keyboard"
                            size={16}
                            color={Colors.greyDark}
                        />
                        <span style={Sty.text14}>Text Mode</span>
                    </div>
                )}
            </div>
        )
    }

    const renderMaestroChatFooter = () => {
        return (
            <div className="maestroChatFooter" style={sty.maestroChatFooter}>
                <div className="maestroChatFooterInputContainer" style={sty.maestroChatFooterInputContainer}>
                    <InputSymphony
                        className="maestroChatFooterInput"
                        value={messageText}
                        onChange={(text) => setMessageText(text)}
                        placeholder="Ask Maestro AI anything..."
                        onEnter={() => {
                            if (messageText.trim()) {
                                sendMessage(messageText)
                                setMessageText('')
                            }
                        }}
                        disabled={isProcessing || isLoadingMessages}
                    />
                    <Icon
                        className="maestroChatFooterSendIcon"
                        icon="send"
                        size={42}
                        isCircle
                        onClick={() => {
                            if (messageText.trim()) {
                                sendMessage(messageText)
                                setMessageText('')
                            }
                        }}
                        style={styx(
                            {},
                            (isProcessing || isLoadingMessages) ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                        )}
                    />
                </div>

                {renderVoiceControls()}

                <div className="tagMaestroContainer" style={sty.tagMaestroContainer}>
                    {maestroTags.map((tag) => {
                        return (
                            <TagSymphony
                                key={tag}
                                className="tagMaestro"
                                title={tag}
                                onClick={() => {
                                    if (!isProcessing && !isLoadingMessages) {
                                        sendMessage(tag)
                                    }
                                }}
                                style={styx(
                                    {},
                                    (isProcessing || isLoadingMessages) ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                                )}
                            />
                        )
                    })}
                </div>
            </div>
        )
    }

    const renderVoiceModeUI = () => {
        return (
            <div className="voiceModeContainer" style={sty.voiceModeContainer}>
                <div className="voiceModeHeader" style={sty.voiceModeHeader}>
                    <Icon
                        className="voiceModeHeaderIcon"
                        icon="maestroBadgeImage"
                        size={32}
                    />
                    <div className="voiceModeHeaderTitle" style={styx(Sty.text18bold, { color: Colors.purple })}>
                        Maestro Voice Assistant
                    </div>
                    <div
                        className="voiceModeCloseButton"
                        style={sty.voiceModeCloseButton}
                        onClick={() => {
                            setIsVoiceMode(false)
                            handleEndVoiceSession()
                        }}
                    >
                        <Icon
                            className="voiceModeCloseIcon"
                            icon="close"
                            size={20}
                            color={Colors.greyDark}
                        />
                    </div>
                </div>

                <div className="voiceModeContent" style={sty.voiceModeContent}>
                    <div className="voiceModeBubble" style={styx(
                        sty.voiceModeBubble,
                        isVoiceListening ? sty.voiceModeBubbleListening : {},
                        isVoiceSpeaking ? sty.voiceModeBubbleSpeaking : {},
                        isProcessing ? sty.voiceModeBubbleProcessing : {}
                    )}>
                        <div className="voiceModeBubbleInner" style={sty.voiceModeBubbleInner}>
                            {isVoiceListening && !isProcessing && !isVoiceSpeaking && (
                                <div className="voiceModeMicIndicator" style={sty.voiceModeMicIndicator}>
                                    <Icon
                                        className="voiceModeMicIcon"
                                        icon="maestroBadgeImage"
                                        size={32}
                                        color={Colors.purple}
                                    />
                                    <div style={styx(Sty.text14, { color: Colors.purple })}>Listening...</div>
                                </div>
                            )}

                            {isProcessing && (
                                <div className="voiceModeProcessingIndicator" style={sty.voiceModeProcessingIndicator}>
                                    <div className="loadingIndicator" style={sty.loadingIndicator}>
                                        <div className="loadingDot" style={styx(sty.loadingDot, sty.loadingDot1)}></div>
                                        <div className="loadingDot" style={styx(sty.loadingDot, sty.loadingDot2)}></div>
                                        <div className="loadingDot" style={styx(sty.loadingDot, sty.loadingDot3)}></div>
                                    </div>
                                    <div style={styx(Sty.text14, { color: Colors.purple })}>Processing...</div>
                                </div>
                            )}

                            {isVoiceSpeaking && (
                                <div className="voiceModeSpeakingIndicator" style={sty.voiceModeSpeakingIndicator}>
                                    <div className="speakingIndicator" style={sty.speakingIndicator}>
                                        <div className="speakingWave" style={styx(sty.speakingWave, sty.speakingWave1)}></div>
                                        <div className="speakingWave" style={styx(sty.speakingWave, sty.speakingWave2)}></div>
                                        <div className="speakingWave" style={styx(sty.speakingWave, sty.speakingWave3)}></div>
                                    </div>
                                    <div style={styx(Sty.text14, { color: Colors.purple })}>Maestro is speaking...</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {transcriptText && (
                        <div className="voiceModeTranscript" style={sty.voiceModeTranscript}>
                            "{transcriptText}"
                        </div>
                    )}
                </div>

                <div className="voiceModeControls" style={sty.voiceModeControls}>
                    {/* {renderVolumeControl()} */}

                    <div
                        className="voiceModeTextButton"
                        style={sty.voiceModeTextButton}
                        onClick={() => setIsVoiceMode(false)}
                    >
                        <Icon
                            className="voiceModeTextIcon"
                            icon="edit"
                            size={16}
                            color={Colors.greyDark}
                        />
                        <span style={Sty.text14}>Switch to Text Mode</span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={clsx("maestroUserChatComponent", className)}
            style={styx(
                sty.maestroUserChatComponent,
                isChatOpen ? {
                    pointerEvents: 'auto',
                } : {
                    pointerEvents: 'none',
                },
                style
            )}
        >
            <div className="maestroUserChatOverlay" style={styx(
                sty.maestroUserChatOverlay,
                isChatOpen ? {
                    opacity: 1,
                } : {
                    opacity: 0,
                }
            )}></div>

            <div
                className="maestroUserChatInner"
                style={styx(
                    sty.maestroUserChatInner,
                    { height: cssCalc(32) },
                    isChatOpen ? {
                        opacity: 1,
                        visibility: 'visible',
                    } : {
                        opacity: 0,
                        visibility: 'hidden',
                    },
                    mobileView ? sty.maestroUserChatInnerMobile : {}
                )}
            >
                {isVoiceMode ? (
                    renderVoiceModeUI()
                ) : (
                    <>
                        {renderMaestroChatHeader()}
                        {renderMessages()}
                        {renderMaestroChatFooter()}
                    </>
                )}
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    maestroUserChatComponent: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
        transition: 'all 0.3s ease',
    },
    maestroUserChatOverlay: {
        transition: 'all 0.3s ease',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 999,
    },
    maestroUserChatInner: {
        position: 'fixed',
        right: 8,
        top: 8,
        bottom: 8,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 450,
        borderRadius: 16,
        backgroundColor: 'white',
        marginLeft: 'auto',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
    },
    maestroUserChatInnerMobile: {
        right: 0,
        top: 0,
        bottom: 0,
        left: 0,
        maxWidth: '100%',
        borderRadius: 0,
        margin: 0,
    },
    maestroUserChatHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        gap: 8,
        borderBottom: Sty.border,
    },
    maestroUserChatHeaderActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginLeft: 'auto',
    },
    maestroUserChatHeaderActionButton: {
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1000,
        backgroundColor: Colors.greyLight,
        cursor: 'pointer',
    },
    maestroUserChatHeaderCloseIconCircle: {
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1000,
        backgroundColor: Colors.greyLight,
    },
    maestroMessages: {
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 8,
        overflowY: 'auto',
        padding: 16,
        position: 'relative',
    },
    maestroChatFooter: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        borderTop: Sty.border,
        gap: 8,
    },
    maestroChatFooterInputContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    tagMaestroContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        overflowX: 'auto',
        scrollbarWidth: 'none',
    },
    voiceControlsContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        marginBottom: 8,
    },
    voiceControlButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 8,
        backgroundColor: Colors.greyLight,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    voiceControlButtonActive: {
        backgroundColor: Colors.purpleLight,
    },
    voiceVolumeContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    volumeSlider: {
        width: 80,
        height: 4,
        appearance: 'none',
        backgroundColor: Colors.greyLight,
        borderRadius: 2,
        outline: 'none',
    },
    statusIndicatorContainer: {
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 8,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 10,
    },
    statusMessage: {
        ...Sty.text14,
        color: Colors.greyDark,
        textAlign: 'center',
    },
    transcriptPreview: {
        ...Sty.text14,
        color: Colors.purple,
        fontStyle: 'italic',
        marginTop: 4,
        textAlign: 'center',
    },
    loadingIndicator: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        marginBottom: 4,
    },
    loadingDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: Colors.purple,
        opacity: 0.6,
        animation: 'pulse 1.5s infinite ease-in-out',
    },
    loadingDot1: {
        animationDelay: '0s',
    },
    loadingDot2: {
        animationDelay: '0.2s',
    },
    loadingDot3: {
        animationDelay: '0.4s',
    },
    speakingIndicator: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        marginBottom: 4,
    },
    speakingWave: {
        width: 3,
        height: 12,
        borderRadius: 1,
        backgroundColor: Colors.purple,
        animation: 'soundWave 1s infinite ease-in-out',
    },
    speakingWave1: {
        animationDelay: '0s',
        height: 8,
    },
    speakingWave2: {
        animationDelay: '0.2s',
        height: 16,
    },
    speakingWave3: {
        animationDelay: '0.4s',
        height: 10,
    },
    exitVoiceModeButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 8,
        backgroundColor: Colors.greyLight,
        cursor: 'pointer',
        marginLeft: 'auto',
    },
    voiceModeContainer: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        height: '100%',
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
    },
    voiceModeHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        gap: 8,
        borderBottom: Sty.border,
    },
    voiceModeCloseButton: {
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1000,
        backgroundColor: Colors.greyLight,
        cursor: 'pointer',
        marginLeft: 'auto',
    },
    voiceModeContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflow: 'auto',
    },
    voiceModeBubble: {
        width: 160,
        height: 160,
        borderRadius: '50%',
        backgroundColor: Colors.greyLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
    },
    voiceModeBubbleInner: {
        width: 140,
        height: 140,
        borderRadius: '50%',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
    },
    voiceModeBubbleListening: {
        boxShadow: `0 0 0 10px rgba(147, 51, 234, 0.1)`,
        animation: 'pulse 2s infinite',
    },
    voiceModeBubbleSpeaking: {
        boxShadow: `0 0 0 10px rgba(147, 51, 234, 0.1)`,
        animation: 'pulse 1s infinite',
    },
    voiceModeBubbleProcessing: {
        boxShadow: `0 0 0 10px rgba(147, 51, 234, 0.1)`,
    },
    voiceModeMicIndicator: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
    },
    voiceModeProcessingIndicator: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
    },
    voiceModeSpeakingIndicator: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
    },
    voiceModeTranscript: {
        marginTop: 16,
        padding: 12,
        backgroundColor: Colors.greyLight,
        borderRadius: 12,
        maxWidth: '100%',
        textAlign: 'center',
        ...Sty.text14,
        color: Colors.greyDark,
        fontStyle: 'italic',
    },
    voiceModeControls: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        borderTop: Sty.border,
    },
    voiceModeVolumeContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    voiceModeVolumeSlider: {
        width: 80,
        height: 4,
        appearance: 'none',
        backgroundColor: Colors.greyLight,
        borderRadius: 2,
        outline: 'none',
    },
    voiceModeTextButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 8,
        backgroundColor: Colors.greyLight,
        cursor: 'pointer',
    },
}

export default MaestroUserChat